import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import useDarkMode from 'contexts/ThemeProvider';
import { navigate } from 'gatsby';
import { ChangeEvent, FC, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-use';

import Container from 'quarks/Container';
import Flex from 'quarks/Flex';
import Heading from 'quarks/Heading';
import Image from 'quarks/Image';
import Link from 'quarks/Link';
import Paragraph from 'quarks/Paragraph';
import Text from 'quarks/Text';

import Pagination from 'molecules/Pagination/NumberedPagination';
import TextField from 'molecules/TextField/TextField';

import Breadcrumbs from 'components/Breadcrumbs/Breadcrumbs';
import ComponentButton from 'components/Button/ComponentButton';
import ResourceCard from 'components/Cards/ResourceCard';
import useResourcePosts from 'components/Resources/utils/useResourcePosts';

import richTextParser from 'utils/richTextParser';

import type { ContentfulComponentSingleInstance, ContentfulTemplateResource } from 'graphqlTypes';

type ResourceLandingProps = ContentfulComponentSingleInstance & {
  backgroundIsDark?: boolean;
  pageType?: string;
  baseFolder?: string;
};

const scrollPosition = 'scroll-margin-top: 150px;';

const textWrapCSS = `
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ResourceLandingPage: FC<ResourceLandingProps> = ({ heading, body, pageType, baseFolder = '/resources/' }) => {
  const lowercasePageType = pageType?.toLowerCase()?.replace(/ +/g, '-');
  const { cases, eguides, infographics, reports, tipsTools, webinars, videos } = useResourcePosts();

  const includesHero = pageType !== 'Case Studies';

  const dataMap = {
    'Case Studies': cases,
    Eguides: eguides,
    Infographics: infographics,
    Reports: reports,
    'Tips and Tools': tipsTools,
    Videos: videos,
    Webinars: webinars,
  };

  const posts: ContentfulTemplateResource[] = dataMap[pageType]?.nodes || [];
  const { pathname } = useLocation();

  const [filtered, setFiltered] = useState(posts);
  const [isDark] = useDarkMode();
  const [params, setParams] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearch, setSearch] = useState(false);
  const [page, setPage] = useState(1);

  const wrapper = useRef(null);

  const perCount = 12;

  const urlParams = pathname?.split('/') || [];
  const currentPage = pathname?.includes('/page/') ? urlParams[urlParams.length - 1] : 1;
  const baseUrl = `${baseFolder}${lowercasePageType}`;

  const filterByQueries = (term: string, postArr: any) =>
    term === ''
      ? postArr
      : postArr.filter(
          (post: ContentfulTemplateResource) =>
            post?.title?.toLowerCase().includes(term.toLowerCase()) ||
            (post?.excerpt?.raw &&
              documentToPlainTextString(JSON.parse(post.excerpt.raw))?.toLowerCase().includes(term.toLowerCase())),
        );

  const goPage = (pg: number, navigationState = {}) => {
    if (params) {
      setPage(pg);
      const position = document.querySelector('#listing-section');
      if (position) {
        window.scrollTo({
          top: position.getBoundingClientRect().top + window.scrollY - 425,
          behavior: 'smooth',
        });
      }
    } else {
      navigate(`${baseUrl}/${pg === 1 ? '' : `page/${pg}`}#listing-section`, {
        state: navigationState,
      });
    }
  };

  const handleSearch = (clearSearch?: boolean) => {
    setParams(searchTerm);
    if (clearSearch) {
      setSearch(false);
      setParams('');
      setSearchTerm('');
      setPage(1);
      setFiltered(filterByQueries('', posts));
      window.history.pushState({}, '', baseUrl);

      return;
    }

    setSearch(true);
    setPage(1);
    const modified = filterByQueries(searchTerm, posts);
    setFiltered(modified);
    goPage(1, {
      searchTerm,
      isSearch: true,
      params: searchTerm,
    });
  };

  const countCurrent = useMemo(() => {
    const total = filtered?.length || 0;
    const totalPages = Math.floor(total / perCount) + (total % perCount ? 1 : 0);
    const start = perCount * (page - 1) + 1;
    if (page < totalPages) {
      return `${start}-${perCount * page}`;
    } else {
      return `${start}-${total}`;
    }
  }, [page, filtered]);

  useEffect(() => {
    const prevParams = window?.history?.state?.params || params;
    const prevSearchTerm = window?.history?.state?.searchTerm || searchTerm;
    const prevIsSearch = window?.history?.state?.isSearch || isSearch;
    const modified = filterByQueries(prevSearchTerm, posts);

    setParams(prevParams);
    setSearch(prevIsSearch);
    setSearchTerm(prevSearchTerm);
    setFiltered(modified);
  }, []);

  useEffect(() => {
    setPage(Number(currentPage));
  }, [currentPage]);

  const breadcrumbs = useMemo(
    () =>
      isSearch
        ? [
            { label: 'Resources', link: '/resources' },
            {
              label: pageType,
              link: `/resources/${lowercasePageType}`,
              onClick: () => handleSearch(true),
            },
            { label: 'Search Results', link: '' },
          ]
        : [
            { label: 'Resources', link: '/resources' },
            { label: pageType, link: `/resources/${lowercasePageType}` },
          ],
    [isSearch],
  );

  const handleChange = (e: ChangeEvent) => {
    const value = (e.target as HTMLInputElement).value;
    setSearchTerm(value);
    if (value === '') {
      setSearch(false);
      setFiltered(filtered);
      handleSearch(true);
    }
  };

  return (
    <Flex flexDirection="column" id="stop">
      {includesHero && (
        <Flex flexDirection="column">
          <Flex
            position="relative"
            flexDirection="column"
            width="100%"
            maxWidth="100%"
            marginX="auto"
            paddingTop={32}
            paddingBottom={96}
            gap="40px"
            zIndex="1"
            xs={{ paddingTop: 0 }}
            md={{ maxWidth: '100%', gap: '64px' }}
          >
            <Flex marginBottom={24} md={{ marginBottom: 32 }}>
              <Breadcrumbs breadcrumbs={breadcrumbs} isDark={true} />
            </Flex>
            <Flex
              flexDirection="column"
              justifyContent="space-between"
              alignItems="flex-end"
              width="100%"
              gap="32px"
              xl={{ flexDirection: 'row' }}
            >
              <Flex flexDirection="column" gap="16px" width="100%" maxWidth="100%" xl={{ maxWidth: '570px' }}>
                {heading && (
                  <Heading
                    fontSize="h3"
                    lineHeight="h3"
                    textColor="common-white"
                    fontFamily="secondaryFont"
                    sm={{ fontSize: 'h2', lineHeight: 'h2' }}
                    lg={{ fontSize: 'h1', lineHeight: 'h1' }}
                  >
                    {heading}
                  </Heading>
                )}
                {body && (
                  <Flex
                    textColor="common-white"
                    fontSize="textMd"
                    sm={{ fontSize: 'textMd' }}
                    lg={{ fontSize: 'textLg' }}
                  >
                    {richTextParser(body)}
                  </Flex>
                )}
              </Flex>
              <Flex
                flexDirection="column"
                justifyContent="flex-end"
                gap="12px"
                width="100%"
                maxWidth="100%"
                onKeyDown={event => event.keyCode === 13 && handleSearch()}
                xl={{ flexDirection: 'row', maxWidth: '470px' }}
              >
                <TextField
                  type="text"
                  name="text"
                  onChange={handleChange}
                  value={params}
                  placeholder={`search ${pageType?.toLowerCase()}`}
                />
                <Flex>
                  <ComponentButton label="Search" handleClick={() => handleSearch()} isButton />
                </Flex>
              </Flex>
            </Flex>
            {!isSearch && filtered[0] && (
              <Link href={`/resources/${lowercasePageType}/${filtered[0]?.slug}`} textDecoration="none">
                <Flex
                  flexDirection="column"
                  justifyContent="space-between"
                  borderRadius="16px"
                  overflowX="hidden"
                  overflowY="hidden"
                  backgroundColor="common-white"
                  boxShadow="xl"
                  lg={{ flexDirection: 'row' }}
                >
                  {filtered[0]?.seo?.openGraphImage && (
                    <Flex width="100%" lg={{ width: '50%' }} xl={{ width: '830px' }}>
                      <Image
                        image={filtered[0]?.seo?.openGraphImage}
                        alt={filtered[0]?.title || ''}
                        width="100%"
                        height="100%"
                        objectFit="cover"
                        css={{
                          backdropFilter: isDark ? 'blur(10px)' : 'blur(15px)',
                        }}
                      />
                    </Flex>
                  )}
                  <Flex
                    flexDirection="column"
                    justifyContent="flex-start"
                    width="100%"
                    maxWidth="100%"
                    gap="16px"
                    paddingAll={24}
                    lg={{ maxWidth: '50%', paddingY: 32, paddingX: 40, gap: '24px' }}
                    xl={{ maxWidth: '450px' }}
                    css={`
                      position: relative;

                      button {
                        padding: 0;

                        @media (min-width: 991px) {
                          position: absolute;
                          bottom: 20px;
                        }
                      }
                    `}
                  >
                    {filtered[0]?.title && (
                      <Heading as="h4" textStyle="h4" textColor="gray-900" md={{ textStyle: 'h3' }}>
                        {filtered[0].title}
                      </Heading>
                    )}
                    {filtered[0]?.excerpt && (
                      <Paragraph
                        fontSize="textSm"
                        lineHeight="textSm"
                        textColor="gray-700"
                        marginBottom={0}
                        css={textWrapCSS}
                      >
                        {richTextParser(filtered[0].excerpt)}
                      </Paragraph>
                    )}
                    <ComponentButton
                      link={`/resources/${lowercasePageType}/${filtered[0]?.slug}`}
                      label="Read More"
                      endIcon="Arrow Right"
                      buttonColor="Gray 900 Link"
                    />
                  </Flex>
                </Flex>
              </Link>
            )}
          </Flex>
          {isSearch ? (
            <Flex
              width="100%"
              height="100%"
              maxHeight="465px"
              position="absolute"
              top="0"
              left="0"
              zIndex="-1"
              backgroundColor="navy-900"
              md={{ maxHeight: '560px' }}
              lg={{ maxHeight: '500px' }}
              css={`
                clip-path: polygon(0% 50%, 0% 100%, 100% 80%, 100% 0%, 0% 0%);
              `}
            />
          ) : (
            <Flex
              width="100vw"
              height="100%"
              maxHeight="700px"
              position="absolute"
              backgroundColor="navy-900"
              top="0"
              left="0"
              zIndex="-1"
              md={{ maxHeight: '800px' }}
              lg={{ maxHeight: '600px' }}
            />
          )}
        </Flex>
      )}
      <Container
        id="listing-section"
        css={scrollPosition}
        backgroundColor="common-transparent"
        paddingTop={isSearch ? 64 : 0}
        md={{ paddingX: 24, maxWidth: '770px', marginX: 'auto', paddingTop: isSearch ? 96 : 0 }}
        lg={{ paddingX: 0, maxWidth: 'unset', marginX: 0 }}
      >
        <Flex
          flexDirection="column"
          alignItems="flex-start"
          gap="24px"
          justifyContent="space-between"
          md={{ flexDirection: 'row', alignItems: 'center' }}
          {...(isSearch && { xl: { height: '100px', marginTop: 64 } })}
        >
          <Flex flexDirection="column" gap="16px">
            <Flex
              fontSize="h5"
              lineHeight="h5"
              textColor="gray-900"
              fontWeight="semiBold"
              fontFamily="secondaryFont"
              lg={{ fontSize: 'h4', lineHeight: 'h4' }}
            >
              {isSearch ? (
                <>
                  {filtered.length === 0 ? (
                    <Text>No results for “{params}”</Text>
                  ) : (
                    <Text>Search Results for “{params}”</Text>
                  )}
                </>
              ) : (
                `Latest ${pageType}`
              )}
            </Flex>
            {isSearch && (
              <>
                {filtered.length === 0 ? (
                  <Text textStyle="textSm" textColor="gray-700">
                    Sorry, we were not able to find a match. Try another search?
                  </Text>
                ) : (
                  <Text textStyle="textSm" textColor="gray-700">
                    Showing{' '}
                    <Text fontWeight="bold" textColor="gray-900">
                      {countCurrent}
                    </Text>{' '}
                    of{' '}
                    <Text fontWeight="bold" textColor="gray-900">
                      {filtered.length}{' '}
                    </Text>
                    results
                  </Text>
                )}
              </>
            )}
          </Flex>
        </Flex>
        {filtered.length > 0 && (
          <Flex
            flexDirection="column"
            width="100%"
            maxWidth="100%"
            gap="40px"
            position="relative"
            marginTop={64}
            ref={wrapper}
          >
            <Flex justifyContent="flex-start" flexWrap="wrap" gap="30px">
              {filtered?.slice((page - 1) * perCount, page * perCount).map(post => (
                <Flex
                  key={post?.id}
                  width="100%"
                  maxWidth="100%"
                  md={{ maxWidth: 'calc((100% - 30px) / 2)' }}
                  lg={{ maxWidth: 'calc((100% - 60px) / 3)' }}
                >
                  <ResourceCard pageType={pageType} {...post} backgroundIsDark={false} />
                </Flex>
              ))}
            </Flex>
            {filtered?.length > 12 && (
              <Pagination
                prefix={baseUrl}
                currentPage={page}
                totalPages={Math.ceil(filtered.length / perCount)}
                setCurrentPage={pg => goPage(pg)}
                isSearch={params ? true : false}
              />
            )}
          </Flex>
        )}
      </Container>
    </Flex>
  );
};

export default ResourceLandingPage;
